import React, { useCallback, useState, useEffect } from 'react';
import { Pencil, PlusCircle, Trash2, Cpu } from 'lucide-react';
import { redisConfigurationService, type RedisConfiguration } from '../../../../components/api/redisConfigurationService';
import AddRedisConfigModal from './AddRedisConfigModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';

const Cache: React.FC = () => {
  const [list, setList] = useState<RedisConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editConfig, setEditConfig] = useState<RedisConfiguration | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [configToDelete, setConfigToDelete] = useState<RedisConfiguration | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await redisConfigurationService.getList();
      setList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load Redis configurations.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Redis Configuration</h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure Redis cache connection and settings.
          </p>
        </div>
        <button
          onClick={() => { setEditConfig(null); setShowModal(true); }}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add Redis Config
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

      {loading ? (
        <div className="text-center text-gray-500 py-10">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-center text-gray-500 py-10">No Redis configurations yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {list.map((cfg) => (
            <div
              key={cfg.id}
              className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 shadow hover:border-indigo-500 transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-amber-400" />
                  <h3 className="text-white font-medium text-base">{cfg.name}</h3>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-semibold ${cfg.isActive ? 'bg-green-700 text-green-100' : 'bg-red-700 text-red-100'}`}
                >
                  {cfg.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="text-sm text-gray-300 space-y-1">
                <p>
                  <span className="text-gray-400">Host:</span> {cfg.host ?? '—'}
                </p>
                <p>
                  <span className="text-gray-400">Port:</span> {cfg.port ?? '—'}
                </p>
                <p>
                  <span className="text-gray-400">Database:</span> {cfg.database ?? '—'}
                </p>
                <p>
                  <span className="text-gray-400">Created By:</span> {cfg.createdBy ?? '—'}
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="text-yellow-400 hover:text-yellow-300"
                  title="Edit"
                  onClick={() => { setEditConfig(cfg); setShowModal(true); }}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  className="text-red-400 hover:text-red-300 disabled:opacity-50"
                  title="Delete"
                  disabled={deletingId === cfg.id}
                  onClick={() => setConfigToDelete(cfg)}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <AddRedisConfigModal
          onClose={() => { setShowModal(false); setEditConfig(null); }}
          onSubmit={fetchList}
          initialData={editConfig}
        />
      )}

      {configToDelete && (
        <ConfirmDialog
          title="Delete Redis Configuration"
          message={`Delete "${configToDelete.name}"? This cannot be undone.`}
          onYes={async () => {
            const id = configToDelete.id;
            setConfigToDelete(null);
            setDeletingId(id);
            setError(null);
            try {
              await redisConfigurationService.delete(id);
              await fetchList();
            } catch (e: any) {
              setError(e?.response?.data?.message || e?.message || 'Failed to delete Redis configuration.');
            } finally {
              setDeletingId(null);
            }
          }}
          onCancel={() => setConfigToDelete(null)}
        />
      )}
    </div>
  );
};

export default Cache;
